import { useEffect } from 'react';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { isAnyModalOpen } from './useModalDialog';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

const isVisible = (el) => {
  const style = window.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
};

const getLabel = (el) =>
  el.getAttribute('aria-label') || el.textContent?.trim() || el.getAttribute('title') || el.tagName.toLowerCase();

/**
 * Generic N (next) / P (previous) / O (open) keyboard navigation fallback
 * for pages that don't have a curated list to drive with useListNavigation
 * (e.g. Home, Sponsors, Map, SessionDetail, About, Account, auth pages).
 * Cycles real keyboard focus through every focusable element inside the
 * given container (by default the page's <main>), so AAC switch-scanning
 * software always has a next/previous way to move through a page's links
 * and controls, not just pages with a dedicated list.
 *
 * Unlike useListNavigation, the "current" position is derived from
 * document.activeElement itself rather than separate hook state, so it
 * never drifts out of sync with real Tab presses or clicks.
 */
export function useFocusableNavigation({ containerSelector = '#main-content' } = {}) {
  const { announce } = useAccessibility();

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isAnyModalOpen()) return;
      if (isTypingTarget(document.activeElement)) return;
      const key = event.key.toLowerCase();
      if (key !== 'n' && key !== 'p' && key !== 'o') return;

      const container = document.querySelector(containerSelector);
      if (!container) return;

      const items = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
      if (items.length === 0) return;

      if (key === 'o') {
        const current = document.activeElement;
        if (items.includes(current)) {
          event.preventDefault();
          current.click();
        }
        return;
      }

      event.preventDefault();
      const currentIndex = items.indexOf(document.activeElement);
      let nextIndex;
      if (key === 'n') {
        nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % items.length;
      } else {
        nextIndex = currentIndex === -1 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length;
      }

      const next = items[nextIndex];
      next.focus();
      next.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      announce(getLabel(next));
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [announce, containerSelector]);
}
